import React, { Suspense, useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthContext } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import Login from './pages/auth/Login';
import AuthLayout from './layouts/AuthLayout';
import ComingSoon from './pages/ComingSoon';
import VerificationStatus from './pages/auth/VerificationStatus';
import Verification from './pages/auth/Verification';
import { hasPermission, selectLoader } from './utils/Helpers';
import ProfileCompletion from './pages/auth/ProfileCompletion';
import Home from './pages/projects/Home';
import ClientDirectory from './pages/projects/ClientDirectory';
import MyTeam from './pages/projects/MyTeam';
import ProjectOverview from './pages/projects/ProjectOverview';
import AcceptInvite from './pages/auth/AcceptInvite';
import OrganizationUsers from './pages/projects/OrganizationUsers';
import Settings from './pages/projects/settings/Settings';
import TemplatesLibrary from './pages/projects/TemplatesLibrary';
import Analytics from './pages/projects/Analytics';
import Support from './pages/projects/Support';
import Integrations from './pages/projects/Integrations';
import TermsConditions from './components/TermsConditions';
import Services from './components/Services';
import Features from './pages/auth/welcome/Features';
import Video from './pages/auth/welcome/Video';
import Questions from './pages/auth/welcome/Questions';
import SubPlan from './pages/auth/welcome/SubPlan';
import ProjectBoard from 'pages/projects/kanban/ProjectBoard';
import ProjectBoardTest from 'pages/projects/kanban/ProjectBoardTest';
import ProjectBoards from 'pages/projects/projectBoards/ProjectBoards';
import PlanDetails from 'pages/projects/planDetails/PlanDetails';
import SubSuccess from 'pages/projects/subcription/SubSuccess';
import SubCancel from 'pages/projects/subcription/SubCancel';
import LeadSubCancel from 'pages/projects/subcription/LeadSubCancel';
import ProjectSubUnavailable from 'pages/projects/subcription/ProjectSubUnavailable';
import TimeTracker from 'pages/projects/timeTracker/TimeTracker';
import SavedTemplates from 'pages/projects/kanban/SavedTemplates/SavedTemplates';
import TemplatesPreview from 'pages/projects/kanban/SavedTemplates/TemplatesPreview';
import CreateEstimate from 'pages/projects/updatedoverviewtabs/estimationScreens/CreateEstimate';
import ChangeOrderDetails from 'pages/projects/updatedoverviewtabs/estimationScreens/ChangeOrderDetails';

const RequireAuth = ({ children }) => {
  const { token } = useAuth(AuthContext);
  if (!token) {
    return <Navigate to={'/login'} />;
  }
  return (
    <>
      <Suspense fallback={selectLoader(50)}>
        <Outlet />
      </Suspense>
    </>
  );
};

// New protected route for isUser1
const RequireUser1 = ({ children }) => {
  const { isUser1 } = useAuth(AuthContext);

  if (!isUser1) {
    return <Navigate to={'/projects'} />;
  }
  return (
    <Suspense fallback={selectLoader(50)}>
      <Outlet />
    </Suspense>
  );
};

// Route-level permission guard; `allow` is computed by the caller
const RequirePermission = ({ allow }) => (allow ? <Outlet /> : <Navigate to={'/projects'} replace />);

const RoutesComponent = () => {
  let { token, userRole, isUser1, subInfo, isOwner } = useAuth(AuthContext);
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 5000); // 5 seconds

    return () => clearTimeout(timer);
  }, []);
  if (subInfo === null && token && isUser1 && showLoader)
    return <div className="refresh-loader">{selectLoader(70)}</div>;

  return (
    <Router>
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path="/" element={token ? <Navigate to="/projects" /> : <Navigate to="/login" />} />
          {/* <Route path="/" element={<Navigate to="/project-boards" />} /> */}
          <Route path="/login" element={<Login />} />
          <Route path="/verification" element={<Verification />} />
          <Route path="/complete-profile" element={<ProfileCompletion />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvite />} />
          <Route path="/verification-status" element={<VerificationStatus />} />
        </Route>
        <Route path="/comingsoon" element={<ComingSoon />} />
        <Route path="*" element={<Navigate to={token ? '/projects' : '/login'} />} />
        <Route path="/" element={<RequireAuth />} />
        <Route path="*" element={<Navigate to={token ? '/projects' : '/login'} />} />
        <Route path="/privacy-policy" element={<TermsConditions />} />
        <Route path="/features" element={<Features />} />
        <Route path="/video" element={<Video />} />
        <Route path="/Questions" element={<Questions />} />
        <Route path="/plans" element={<SubPlan />} />
        <Route path="/subscription-success" element={<SubSuccess />} />
        <Route path="/project-subscription-unavailable" element={<ProjectSubUnavailable />} />
        <Route path="/subscription-cancelled" element={<SubCancel />} />
        <Route path="/services" element={<Services />} />

        {/* <Route path="/timetracker" element={<TimeTracker />} /> */}
        <Route path="/Calendar" element={<Navigate to="/projects" replace />} />
        {/* <Route path="/services" element={<RequireAuth />} /> */}
        <Route path="/" element={<RequireAuth />}>
          <Route path="/projects" element={<Home />} />
          <Route element={<RequirePermission allow={hasPermission(userRole, 'plan_details')} />}>
            <Route path="/my-plan" element={<PlanDetails />} />
          </Route>
          {((subInfo?.isActive && isUser1) || !isUser1) && !(isUser1 && !isOwner && !subInfo?.isActive) && (
            <>
              <Route path="/project-boards" element={<ProjectBoards />} />
              <Route path="/project-board/board/:projectId" element={<ProjectBoard />} />
              <Route path="/project-board/board/child/:projectId/:childBoardId" element={<ProjectBoard />} />
              <Route path="/project-board/board/:projectId/:milestoneId" element={<ProjectBoard />} />
              <Route path="/project-board/board/:projectId/:milestoneId/:subtaskId" element={<ProjectBoard />} />
              <Route path="/project-board/project/:projectId" element={<ProjectBoard />} />
              <Route path="/project-board/project/:projectId/:notificationId" element={<ProjectBoard />} />
              <Route path="/project-board/project/child/:projectId/:childBoardId" element={<ProjectBoard />} />
              <Route path="/project-board/project/:projectId/:milestoneId" element={<ProjectBoard />} />
              <Route path="/project-board/project/:projectId/:milestoneId/:subtaskId" element={<ProjectBoard />} />
              <Route path="/project-board/:projectId" element={<ProjectBoard />} />
              <Route path="/project-board/:projectId/:milestoneId" element={<ProjectBoard />} />
              <Route path="/project-board/:projectId/:milestoneId/:subtaskId" element={<ProjectBoard />} />
              <Route path="/project-board/project-overview/:projectId/:notificationId" element={<ProjectBoard />} />
              <Route path="/project-templates/:templateId" element={<TemplatesPreview />} />
              <Route path="/project-board" element={<ProjectBoard />} />
              <Route path="/savedtemplates" element={<SavedTemplates />} />
              <Route element={<RequirePermission allow={hasPermission(userRole, 'modify_team')} />}>
                <Route path="/myteam" element={<MyTeam />} />
              </Route>
              <Route element={<RequirePermission allow={hasPermission(userRole, 'add_qoute')} />}>
                <Route path="/create-estimate" element={<CreateEstimate />} />
              </Route>
              <Route element={<RequirePermission allow={hasPermission(userRole, 'change_order')} />}>
                <Route path="/change-order/new" element={<ChangeOrderDetails />} />
              </Route>
              <Route path="/change-order/:requestId" element={<ChangeOrderDetails />} />
              <Route path="/overview" element={<Navigate to="/projects" replace />} />
              <Route path="/overview/*" element={<Navigate to="/projects" replace />} />
              <Route element={<RequireUser1 />}>
                <Route path="/templates" element={<TemplatesLibrary />} />
                <Route path="/analytics" element={<Analytics />} />
              </Route>
              <Route path="/support" element={<Support />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route element={<RequireUser1 />}>
                {hasPermission(userRole, 'create_projects') && (
                  <>
                    <Route path="/create-project" element={<ProjectOverview />} />
                    <Route path="/create-project/:projectWithContract" element={<ProjectOverview />} />
                  </>
                )}
                {hasPermission(userRole, 'create_projects') && (
                  <Route path="/create-with-contract" element={<ProjectOverview />} />
                )}
                {isUser1 && <Route path="/update-project/:projectId" element={<ProjectOverview />} />}
                <Route element={<RequirePermission allow={hasPermission(userRole, 'modify_team')} />}>
                  <Route path="/client-directory" element={<ClientDirectory />} />
                  <Route path="/client-directory/Client" element={<OrganizationUsers />} />
                </Route>
              </Route>{' '}
            </>
          )}
        </Route>
      </Routes>
    </Router>
  );
};

export default RoutesComponent;
